import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MinimalInput from '../../components/MinimalInput/MinimalInput';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import CustomButton from '../../components/CustomButton/CustomButton';
import AuthNavBar from '../../components/layout/AuthNavBar';
import { useAuthMode } from '../../redux/hooks';
import {
  loginUser,
  clearError,
  selectIsLoading,
  selectAuthError,
  selectIsAuthenticated,
} from '../../redux/slices/authSlice';
import { navigateToDashboard } from '../../redux/slices/uiSlice';
import { isValidEmail, isLoginBlocked, getBlockTimeRemaining } from '../../utils/authUtils';

function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLawyer, setClientMode, setLawyerMode } = useAuthMode();

  // Redux selectors
  const isLoading = useSelector(selectIsLoading);
  const authError = useSelector(selectAuthError);
  const isAuthenticated = useSelector(selectIsAuthenticated);

  // Estado local para el formulario
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loginBlocked, setLoginBlocked] = useState(false);
  const [blockTimeRemaining, setBlockTimeRemaining] = useState(0);

  // Redirección simple si ya está autenticado
  useEffect(() => {
    if (isAuthenticated) {
      // Usar Redux para manejar la navegación
      dispatch(navigateToDashboard());
      // Navegar usando React Router
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate, dispatch]);

  // Verificar bloqueo al cargar el componente
  useEffect(() => {
    const blocked = isLoginBlocked();
    setLoginBlocked(blocked);

    if (blocked) {
      const timeRemaining = getBlockTimeRemaining();
      setBlockTimeRemaining(timeRemaining);

      // Actualizar el tiempo restante cada minuto
      const interval = setInterval(() => {
        const remaining = getBlockTimeRemaining();
        setBlockTimeRemaining(remaining);

        if (remaining <= 0) {
          setLoginBlocked(false);
          clearInterval(interval);
        }
      }, 60000);

      return () => clearInterval(interval);
    }
  }, []);

  const handleInputChange = useCallback(
    field => e => {
      setFormData(prev => ({
        ...prev,
        [field]: e.target.value,
      }));
      // Limpiar error cuando el usuario comience a escribir
      if (authError) {
        dispatch(clearError());
      }
    },
    [authError, dispatch]
  );

  // Validación de email usando la utilidad
  const isValidEmailUtil = useCallback(email => {
    return isValidEmail(email);
  }, []);

  // Validación de campos
  const validateForm = useCallback(() => {
    const errors = [];

    if (!formData.username.trim()) {
      errors.push('El correo electrónico es requerido');
    } else if (!isValidEmailUtil(formData.username.trim())) {
      errors.push('Ingrese un correo electrónico válido');
    }

    if (!formData.password.trim()) {
      errors.push('La contraseña es requerida');
    } else if (formData.password.length < 6) {
      errors.push('La contraseña debe tener al menos 6 caracteres');
    }

    return errors;
  }, [formData.username, formData.password, isValidEmailUtil]);

  const onLogin = useCallback(
    async e => {
      e.preventDefault();
      if (isLoading) return;

      // Verificar si está bloqueado
      if (isLoginBlocked()) {
        return;
      }

      // Validar campos antes de proceder
      const validationErrors = validateForm();
      if (validationErrors.length > 0) {
        // Usar una acción custom para manejar errores de validación
        dispatch(clearError());
        // Aquí podrías crear una acción para errores de validación
        return;
      }

      // Limpiar errores previos
      dispatch(clearError());

      try {
        // Usar el thunk de Redux para login
        await dispatch(
          loginUser({
            email: formData.username.trim(),
            password: formData.password,
            userType: isLawyer ? 'lawyer' : 'client',
          })
        ).unwrap();

        // Si llegamos aquí, el login fue exitoso
        // Usar Redux para manejar la navegación
        dispatch(navigateToDashboard());
        // Redirigir a dashboard unificado
        navigate('/dashboard', { replace: true });
      } catch (error) {
        // Los errores ya se manejan en el slice
        console.error('Login error:', error);
      }
    },
    [formData.username, formData.password, isLawyer, isLoading, navigate, validateForm, dispatch]
  );

  const handleUserType = useCallback(
    type => {
      if (type === 'lawyer') {
        setLawyerMode();
      } else {
        setClientMode();
      }
      // Limpiar errores al cambiar tipo de usuario
      dispatch(clearError());
    },
    [setLawyerMode, setClientMode, dispatch]
  );

  const toggleShowPassword = () => setShowPassword(v => !v);

  return (
    <>
      <AuthNavBar />
      <motion.div
        className="flex flex-col items-center justify-start px-4"
        initial={false}
        animate={{ backgroundColor: isLawyer ? '#132A56' : '#fbfcf8' }}
        transition={{ duration: 0.5, ease: 'easeInOut' }}
        style={{ minHeight: 'calc(100vh - 80px)' }}
      >
        <div className="w-full max-w-sm mx-auto flex flex-col gap-6 mt-2">
          <div className="text-center mb-2">
            <h1
              className={`text-5xl font-serif italic font-bold mb-1 ${
                isLawyer ? 'text-white' : 'text-custom-blue'
              }`}
            >
              ¡Bienvenido!
            </h1>
            <h2
              className={`text-3xl font-light mb-6 ${
                isLawyer ? 'text-white' : 'text-gray-900'
              } leading-10 mt-4`}
              style={{ minHeight: '2.5rem' }}
            >
              <AnimatePresence mode="wait">
                <motion.span
                  key={isLawyer ? 'lawyer' : 'client'}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4, ease: 'easeInOut' }}
                  className="inline-block"
                >
                  {isLawyer
                    ? 'Inicia sesión, es hora de defender la justicia'
                    : 'Inicia sesión, tus problemas están resueltos'}
                </motion.span>
              </AnimatePresence>
            </h2>
          </div>

          {/* Tab Navigation */}
          <div className="flex justify-center mb-8">
            <div
              className={`flex relative ${
                isLawyer ? 'border-b border-gray-500' : 'border-b border-gray-300'
              }`}
            >
              <button
                type="button"
                className={`px-8 py-3 text-lg font-medium transition-colors duration-200 relative ${
                  !isLawyer
                    ? isLawyer
                      ? 'text-white'
                      : 'text-black'
                    : isLawyer
                      ? 'text-gray-400'
                      : 'text-gray-500'
                }`}
                onClick={() => handleUserType('client')}
              >
                Soy Cliente
                {!isLawyer && (
                  <div
                    className={`absolute bottom-0 left-0 right-0 h-0.5 transition-all duration-200 ${
                      isLawyer ? 'bg-white' : 'bg-black'
                    }`}
                  />
                )}
              </button>
              <button
                type="button"
                className={`px-8 py-3 text-lg font-medium transition-colors duration-200 relative ${
                  isLawyer
                    ? isLawyer
                      ? 'text-white'
                      : 'text-black'
                    : isLawyer
                      ? 'text-gray-400'
                      : 'text-gray-500'
                }`}
                onClick={() => handleUserType('lawyer')}
              >
                Soy Abogado
                {isLawyer && (
                  <div
                    className={`absolute bottom-0 left-0 right-0 h-0.5 transition-all duration-200 ${
                      isLawyer ? 'bg-white' : 'bg-black'
                    }`}
                  />
                )}
              </button>
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={isLawyer ? 'lawyer-login' : 'client-login'}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            >
              <form onSubmit={onLogin} className="flex flex-col gap-3">
                <MinimalInput
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange('username')}
                  placeholder="Correo"
                  autoComplete="username"
                  className={`rounded-lg px-4 py-3 text-base transition-colors duration-200 ${
                    isLawyer
                      ? 'bg-transparent border border-gray-400 text-white placeholder-gray-300 focus:ring-gray-200'
                      : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-200'
                  } border`}
                />
                <div className="relative">
                  <MinimalInput
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange('password')}
                    placeholder="Contraseña"
                    autoComplete="current-password"
                    className={`rounded-lg px-4 py-3 text-base pr-10 transition-colors duration-200 ${
                      isLawyer
                        ? 'bg-transparent border border-gray-400 text-white placeholder-gray-300 focus:ring-gray-200'
                        : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-200'
                    } border`}
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={toggleShowPassword}
                    className={`absolute right-3 top-6 transform -translate-y-1/2 focus:outline-none transition-colors duration-200 flex items-center justify-center ${
                      isLawyer ? 'text-gray-300' : 'text-gray-500'
                    }`}
                  >
                    {showPassword ? (
                      <svg
                        width="22"
                        height="22"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        viewBox="0 0 24 24"
                      >
                        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    ) : (
                      <svg
                        width="22"
                        height="22"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        viewBox="0 0 24 24"
                      >
                        <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-7 0-11-7-11-7a21.77 21.77 0 0 1 5.06-6.06M1 1l22 22" />
                        <path d="M9.53 9.53A3.001 3.001 0 0 0 12 15a3 3 0 0 0 2.47-5.47" />
                      </svg>
                    )}
                  </button>
                </div>
                {(authError || (loginBlocked && blockTimeRemaining > 0)) && (
                  <div className="text-red-500 text-xs mb-2 text-center">
                    {loginBlocked && blockTimeRemaining > 0
                      ? `Demasiados intentos fallidos. Intente en ${blockTimeRemaining} minutos.`
                      : authError}
                  </div>
                )}
                <div className="flex justify-center mb-7 -mt-4">
                  <button
                    type="button"
                    className={`text-xs font-serif underline transition-colors bg-transparent border-none cursor-pointer ${
                      isLawyer
                        ? 'text-blue-300 hover:text-blue-100'
                        : 'text-blue-700 hover:text-blue-900'
                    }`}
                    onClick={() => {
                      // TODO: Implementar funcionalidad de recuperar contraseña
                      alert('Funcionalidad en desarrollo');
                    }}
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                </div>
                <CustomButton
                  type="submit"
                  text={isLoading ? 'Entrando...' : 'Inicia Sesión'}
                  disabled={isLoading || (loginBlocked && blockTimeRemaining > 0)}
                  className="w-full justify-center py-3 text-base"
                />
              </form>
              <div
                className={`text-center my-12 text-sm ${isLawyer ? 'text-gray-200' : 'text-gray-700'}`}
              >
                ¿Aún no tienes una cuenta?{' '}
                <a
                  href="/signup"
                  className={`underline transition-colors ${
                    isLawyer
                      ? 'text-blue-300 hover:text-blue-100'
                      : 'text-blue-700 hover:text-blue-900'
                  }`}
                >
                  Entra Aquí
                </a>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </>
  );
}

export default Login;
